// ABOUTME: Modal component for avatar upload, camera capture, and image editing
// ABOUTME: Handles file validation, pan/zoom/rotate controls, and circular cropping

import React, { useState, useRef, useEffect } from 'react';
import './AvatarUploadModal.css';

// Constants for avatar requirements
const AVATAR_SIZE = 500;
const PREVIEW_SIZE = 300; // Size of the circular mask in the UI
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB — must match ImageProcessingService.MaxFileSizeBytes
const ACCEPTED_FORMATS = ['image/jpeg', 'image/png', 'image/webp'];
const MIN_ROTATION = -180;
const MAX_ROTATION = 180;
const PAN_ACCELERATION = 1.5; // Movement multiplier for better responsiveness

interface AvatarUploadModalProps {
  isOpen: boolean;
  onSave: (avatarUrl: string | null) => void;
  onClose: () => void;
  currentAvatarUrl?: string;
  entityType?: 'user' | 'child' | 'goal';
  entityId?: number;
}

interface ImageEditState {
  scale: number;
  rotation: number;
  translateX: number;
  translateY: number;
}

const AvatarUploadModal: React.FC<AvatarUploadModalProps> = ({
  isOpen,
  onSave,
  onClose,
  currentAvatarUrl,
  entityType = 'user',
  entityId
}) => {
  const [currentImage, setCurrentImage] = useState<string | null>(null);
  const [editState, setEditState] = useState<ImageEditState>({
    scale: 1,
    rotation: 0,
    translateX: 0,
    translateY: 0
  });
  const [error, setError] = useState<string>('');
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null);
  const [zoomLimits, setZoomLimits] = useState<{ min: number; max: number }>({ min: 50, max: 200 });
  const [isProcessing, setIsProcessing] = useState(false);
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [showCameraView, setShowCameraView] = useState(false);
  const [cameraError, setCameraError] = useState<string>('');
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Validate entity props
  useEffect(() => {
    if (entityType === 'child' && !entityId) {
      console.error('entityId is required when entityType is child');
    }
  }, [entityType, entityId]);

  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal closes
      setCurrentImage(null);
      setEditState({ scale: 1, rotation: 0, translateX: 0, translateY: 0 });
      setError('');
      setIsProcessing(false);
      setShowRemoveConfirmation(false);
      setShowCameraView(false);
      setCameraError('');
      setImageDimensions(null);
      setZoomLimits({ min: 50, max: 200 });
      stopCamera();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stopCamera intentionally omitted; it is stable and adding it would cause extra re-runs
  }, [isOpen]);

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
  };

  const validateFile = (file: File): string | null => {
    if (!ACCEPTED_FORMATS.some(format => file.type === format)) {
      return 'Please select a valid image file (JPG, PNG, WEBP)';
    }
    
    if (file.size > MAX_FILE_SIZE) {
      return `Image must be smaller than ${MAX_FILE_SIZE / (1024 * 1024)}MB`;
    }
    
    return null;
  };

  const resetEditState = () => {
    // Calculate a reasonable initial scale if we have image dimensions
    let initialScale = 1;
    if (imageDimensions && zoomLimits.min) {
      const imageAspectRatio = imageDimensions.width / imageDimensions.height;
      if (imageAspectRatio > 1) {
        // Image is wider - scale by height to fill circle nicely
        initialScale = (PREVIEW_SIZE * 0.8) / imageDimensions.height;
      } else {
        // Image is taller - scale by width to fill circle nicely
        initialScale = (PREVIEW_SIZE * 0.8) / imageDimensions.width;
      }
      initialScale = Math.max(initialScale, zoomLimits.min / 100);
    }
    
    setEditState({ 
      scale: initialScale, 
      rotation: 0, 
      translateX: 0, 
      translateY: 0 
    });
  };

  const calculateZoomLimits = (width: number, height: number) => {
    // Allow extreme zoom-out flexibility - users should be able to see full image
    // Set a very low minimum zoom that lets users zoom way out
    const minScale = 0.02; // 2% minimum - extremely zoomed out
    
    // Calculate maximum zoom based on image resolution - prevent excessive zoom on large images
    const maxDimension = Math.max(width, height);
    
    // Calculate a reasonable max zoom that doesn't go beyond ~1500px effective size
    const reasonableMaxSize = 1500;
    const calculatedMaxScale = reasonableMaxSize / Math.min(width, height);
    
    // Set limits based on image size with calculated max as ceiling
    let maxScale = Math.min(calculatedMaxScale, 3); // Never exceed 3x
    
    if (maxDimension < 800) {
      maxScale = Math.min(maxScale, 4); // Small images can zoom more
    } else if (maxDimension > 3000) {
      maxScale = Math.min(maxScale, 1.5); // Large images get limited zoom
    } else if (maxDimension > 2000) {
      maxScale = Math.min(maxScale, 2); // Medium-large images 
    }
    
    return {
      min: Math.ceil(minScale * 100), // 2%
      max: Math.ceil(maxScale * 100)
    };
  };

  const validateImageDimensions = (imageUrl: string): Promise<boolean> => {
    return new Promise((resolve) => {
      try {
        const img = new Image();
        img.onload = () => {
          if (img.width < AVATAR_SIZE || img.height < AVATAR_SIZE) {
            setError(`Image must be at least ${AVATAR_SIZE}x${AVATAR_SIZE} pixels`);
            URL.revokeObjectURL(imageUrl);
            resolve(false);
            return;
          }
          
          // Store image dimensions and calculate zoom limits
          setImageDimensions({ width: img.width, height: img.height });
          const limits = calculateZoomLimits(img.width, img.height);
          setZoomLimits(limits);
          
          // Set initial scale to a reasonable "fit" size that fills circle nicely
          // Calculate a good initial scale that fills the circle without being too zoomed
          const imageAspectRatio = img.width / img.height;
          let initialScale: number;
          if (imageAspectRatio > 1) {
            // Image is wider - scale by height to fill circle nicely
            initialScale = (PREVIEW_SIZE * 0.8) / img.height; // 80% fill
          } else {
            // Image is taller - scale by width to fill circle nicely
            initialScale = (PREVIEW_SIZE * 0.8) / img.width; // 80% fill
          }
          
          setEditState(prev => ({
            ...prev,
            scale: Math.max(initialScale, limits.min / 100), // Don't go below minimum
            translateX: 0,
            translateY: 0
          }));
          
          resolve(true);
        };
        img.onerror = () => {
          setError('Failed to load image');
          URL.revokeObjectURL(imageUrl);
          resolve(false);
        };
        img.src = imageUrl;
      } catch {
        // Fallback for testing environments - set reasonable defaults
        setImageDimensions({ width: 1000, height: 1000 });
        setZoomLimits({ min: 50, max: 300 });
        resolve(true);
      }
    });
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError('');

    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      const imageUrl = URL.createObjectURL(file);
      const isValidDimensions = await validateImageDimensions(imageUrl);
      
      if (isValidDimensions) {
        setCurrentImage(imageUrl);
        resetEditState();
      }
    } catch {
      setError('Failed to process image');
    }
  };

  const initializeCamera = async (): Promise<boolean> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'user',
          width: { ideal: 1280, min: 640 },
          height: { ideal: 720, min: 480 }
        } 
      });
      setCameraStream(stream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      return true;
    } catch {
      setCameraError('Camera permission was denied. Try uploading a photo instead.');
      return false;
    }
  };

  const handleCameraCapture = async () => {
    setCameraError('');
    setShowCameraView(true);
    
    const cameraInitialized = await initializeCamera();
    if (!cameraInitialized) {
      setShowCameraView(false);
    }
  };

  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const video = videoRef.current;
    const context = canvas.getContext('2d');
    
    if (!context) return;

    // Ensure captured image meets minimum size requirements
    const minDimension = Math.max(video.videoWidth, video.videoHeight);
    const scaleFactor = minDimension < AVATAR_SIZE ? AVATAR_SIZE / minDimension : 1;
    
    canvas.width = video.videoWidth * scaleFactor;
    canvas.height = video.videoHeight * scaleFactor;
    
    // Draw video to canvas with scaling if needed
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    const imageUrl = canvas.toDataURL('image/jpeg');
    
    // Validate dimensions for camera captures (same as file uploads)
    const isValidDimensions = await validateImageDimensions(imageUrl);
    
    if (isValidDimensions) {
      setCurrentImage(imageUrl);
      setShowCameraView(false);
      stopCamera();
      // Don't call resetEditState() here - let validateImageDimensions handle initial scale
    }
  };

  const startDragging = (event: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ 
      x: event.clientX - (editState.translateX / PAN_ACCELERATION), 
      y: event.clientY - (editState.translateY / PAN_ACCELERATION) 
    });
  };

  const updateImagePosition = (event: React.MouseEvent) => {
    if (!isDragging) return;

    setEditState(prev => ({
      ...prev,
      translateX: (event.clientX - dragStart.x) * PAN_ACCELERATION,
      translateY: (event.clientY - dragStart.y) * PAN_ACCELERATION
    }));
  };

  const stopDragging = () => {
    setIsDragging(false);
  };

  const updateScale = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditState(prev => ({
      ...prev,
      scale: parseFloat(event.target.value) / 100
    }));
  };

  const updateRotation = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditState(prev => ({
      ...prev,
      rotation: parseFloat(event.target.value)
    }));
  };

  const processAndSaveImage = async () => {
    if (!currentImage || !imageDimensions) return;

    setIsProcessing(true);
    
    try {
      // Create a canvas to process the image
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = currentImage;
      });

      canvas.width = AVATAR_SIZE;
      canvas.height = AVATAR_SIZE;
      
      if (ctx) {
        // Clear canvas
        ctx.clearRect(0, 0, AVATAR_SIZE, AVATAR_SIZE);
        
        // Calculate scaling factor from preview to final output
        const scaleFactor = AVATAR_SIZE / PREVIEW_SIZE;
        
        // Apply transformations with proper scaling
        ctx.save();
        ctx.translate(
          AVATAR_SIZE / 2 + (editState.translateX * scaleFactor), 
          AVATAR_SIZE / 2 + (editState.translateY * scaleFactor)
        );
        ctx.rotate((editState.rotation * Math.PI) / 180);
        
        // Apply the combined scaling: user's zoom * preview-to-output scaling
        const finalScale = editState.scale * scaleFactor;
        ctx.scale(finalScale, finalScale);
        
        // Draw image at its actual dimensions (scaling is handled by ctx.scale above)
        ctx.drawImage(img, -imageDimensions.width / 2, -imageDimensions.height / 2, imageDimensions.width, imageDimensions.height);
        ctx.restore();
        
        // Create circular clipping mask
        ctx.globalCompositeOperation = 'destination-in';
        ctx.beginPath();
        ctx.arc(AVATAR_SIZE / 2, AVATAR_SIZE / 2, AVATAR_SIZE / 2, 0, 2 * Math.PI);
        ctx.fill();
        
        const processedImageUrl = canvas.toDataURL('image/jpeg', 0.9);
        onSave(processedImageUrl);
      }
    } catch {
      setError('Failed to process image');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRemoveAvatar = () => {
    setShowRemoveConfirmation(false);
    onSave(null);
  };

  if (!isOpen) return null;

  return (
    <div className="avatar-upload-modal-overlay">
      <div 
        className={`avatar-upload-modal ${window.innerWidth <= 768 ? 'mobile-responsive' : ''}`}
        role="dialog"
        aria-labelledby="avatar-upload-title"
        aria-modal="true"
        data-testid="avatar-upload-modal"
        data-entity-type={entityType}
        data-entity-id={entityId}
      >
        <div className="modal-header">
          <h2 id="avatar-upload-title">Upload Avatar</h2>
          <button 
            className="close-button"
            onClick={onClose}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        <div className="modal-content">
          {error && (
            <div className="error-message" role="alert">
              {error}
            </div>
          )}

          {currentAvatarUrl && !currentImage && (
            <div className="current-avatar">
              <img 
                src={currentAvatarUrl} 
                alt="Current avatar" 
                className="current-avatar-preview"
              />
            </div>
          )}

          {!currentImage && !showCameraView && (
            <div className="upload-options">
              <button
                className="upload-option-button"
                onClick={() => fileInputRef.current?.click()}
              >
                📁 Upload Photo
              </button>
              
              <button
                className="upload-option-button"
                onClick={handleCameraCapture}
              >
                📷 Take Photo
              </button>
              
              {/* This component uses plain CSS (AvatarUploadModal.css) throughout, not MUI —
                  style props here stay as style, not sx, for consistency with the rest of the file */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
                aria-label="Upload photo"
              />
            </div>
          )}

          {cameraError && (
            <div className="camera-error">
              <p>{cameraError}</p>
            </div>
          )}

          {showCameraView && (
            <div className="camera-view">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                data-testid="camera-preview"
                className="camera-preview"
              />
              <button
                className="capture-button"
                onClick={capturePhoto}
              >
                Capture Photo
              </button>
            </div>
          )}

          {currentImage && (
            <div className="image-editor">
              <div className="editor-title">Adjust Your Photo</div>
              
              <div className="image-preview-container">
                <div className="circular-mask" data-testid="circular-mask-overlay">
                  <img
                    src={currentImage}
                    alt="Editable avatar"
                    className="editable-image"
                    data-testid="editable-image"
                    style={{
                      transform: `translate(-50%, -50%) translate(${editState.translateX}px, ${editState.translateY}px) scale(${editState.scale}) rotate(${editState.rotation}deg)`
                    }}
                    onMouseDown={startDragging}
                    onMouseMove={updateImagePosition}
                    onMouseUp={stopDragging}
                    onMouseLeave={stopDragging}
                  />
                </div>
              </div>
              
              <div className="editing-controls">
                <div className="control-group">
                  <label htmlFor="zoom-slider">Zoom</label>
                  <input
                    id="zoom-slider"
                    type="range"
                    min={zoomLimits.min.toString()}
                    max={zoomLimits.max.toString()}
                    value={editState.scale * 100}
                    onChange={updateScale}
                    aria-label="Zoom"
                  />
                </div>
                
                <div className="control-group">
                  <label htmlFor="rotate-slider">Rotate</label>
                  <input
                    id="rotate-slider"
                    type="range"
                    min={MIN_ROTATION.toString()}
                    max={MAX_ROTATION.toString()}
                    value={editState.rotation}
                    onChange={updateRotation}
                    aria-label="Rotate"
                  />
                </div>
              </div>
            </div>
          )}

          {showRemoveConfirmation && (
            <div className="confirmation-dialog">
              <p>Are you sure you want to remove your avatar?</p>
              <p>This action cannot be undone.</p>
              <div className="confirmation-buttons">
                <button
                  className="confirm-button"
                  onClick={handleRemoveAvatar}
                >
                  Yes, Remove It
                </button>
                <button
                  className="cancel-button"
                  onClick={() => setShowRemoveConfirmation(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          {currentAvatarUrl && !showRemoveConfirmation && (
            <button
              className="remove-button"
              onClick={() => setShowRemoveConfirmation(true)}
            >
              Remove Avatar
            </button>
          )}
          
          <div className="action-buttons">
            <button
              className="cancel-button"
              onClick={onClose}
              disabled={isProcessing}
            >
              Cancel
            </button>
            
            {currentImage && (
              <button
                className="save-button"
                onClick={processAndSaveImage}
                disabled={isProcessing}
              >
                {isProcessing ? 'Processing...' : 'Save Avatar'}
              </button>
            )}
          </div>
        </div>

        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </div>
    </div>
  );
};

export default AvatarUploadModal;